import { useState, type ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Webhook,
  Code2,
  Cloud,
  KeyRound,
  Users,
  Hash,
  MessageSquare,
  Mail,
  ShieldAlert,
  ShieldCheck,
  FileCheck2,
  ClipboardCheck,
  Activity,
  Building2,
  UserCog,
  Database,
  Boxes,
  Check,
  ArrowUpRight,
} from 'lucide-react';
import { PageHeader, Card, Badge, cx } from '../components/ui';

// Integrations catalog (scaffold). Webhooks + the Developer API/MCP are live and deep-link to their
// config; everything else is a "Request access" card so the menu reads as a real, broad catalog.
// No backend — request-access is local state for now (a real intake endpoint is a later sub-unit).
type Status = 'live' | 'request';
interface Integ {
  key: string;
  name: string;
  desc: string;
  icon: ComponentType<{ className?: string }>;
  status: Status;
  to?: string;
}
interface Group {
  category: string;
  items: Integ[];
}

const CATALOG: Group[] = [
  {
    category: 'Developer',
    items: [
      {
        key: 'webhooks',
        name: 'Webhooks',
        desc: 'Signed, retried event callbacks to your endpoint.',
        icon: Webhook,
        status: 'live',
        to: '/api-keys?tab=webhooks',
      },
      {
        key: 'api',
        name: 'Developer API & MCP',
        desc: 'Full REST API + Model Context Protocol for agents.',
        icon: Code2,
        status: 'live',
        to: '/docs',
      },
      {
        key: 'zapier',
        name: 'Zapier',
        desc: 'No-code automation across 6,000+ apps.',
        icon: Boxes,
        status: 'request',
      },
    ],
  },
  {
    category: 'Identity & SSO',
    items: [
      {
        key: 'saml',
        name: 'SAML / OIDC SSO',
        desc: 'Single sign-on for operator accounts.',
        icon: KeyRound,
        status: 'request',
      },
      {
        key: 'scim',
        name: 'SCIM Directory Sync',
        desc: 'Auto-provision users and recipients.',
        icon: Users,
        status: 'request',
      },
      {
        key: 'entra',
        name: 'Microsoft Entra ID',
        desc: 'Azure AD users, groups, and SSO.',
        icon: UserCog,
        status: 'request',
      },
    ],
  },
  {
    category: 'Productivity',
    items: [
      {
        key: 'slack',
        name: 'Slack',
        desc: 'Alerts and report digests in channels.',
        icon: Hash,
        status: 'request',
      },
      {
        key: 'teams',
        name: 'Microsoft Teams',
        desc: 'Notifications and reporting to Teams.',
        icon: MessageSquare,
        status: 'request',
      },
      {
        key: 'gworkspace',
        name: 'Google Workspace',
        desc: 'Directory + the "Report Phish" add-on.',
        icon: Mail,
        status: 'request',
      },
      {
        key: 'm365',
        name: 'Microsoft 365',
        desc: 'Directory + the report-message button.',
        icon: Mail,
        status: 'request',
      },
    ],
  },
  {
    category: 'GRC & Compliance',
    items: [
      {
        key: 'vanta',
        name: 'Vanta',
        desc: 'Push phishing-training evidence + reporting to Vanta for SOC 2 / ISO controls.',
        icon: ShieldCheck,
        status: 'request',
      },
      {
        key: 'drata',
        name: 'Drata',
        desc: 'Auto-collect security-awareness completion evidence into Drata.',
        icon: FileCheck2,
        status: 'request',
      },
      {
        key: 'anecdotes',
        name: 'Anecdotes.ai',
        desc: 'Stream simulation results + training records as compliance evidence.',
        icon: ClipboardCheck,
        status: 'request',
      },
    ],
  },
  {
    category: 'Security & SIEM',
    items: [
      {
        key: 'splunk',
        name: 'Splunk',
        desc: 'Stream events to your SIEM.',
        icon: ShieldAlert,
        status: 'request',
      },
      {
        key: 'sentinel',
        name: 'Microsoft Sentinel',
        desc: 'Forward simulation events to Sentinel.',
        icon: Activity,
        status: 'request',
      },
    ],
  },
  {
    category: 'HR & Directory',
    items: [
      {
        key: 'workday',
        name: 'Workday',
        desc: 'Sync employees into Target Lists.',
        icon: Building2,
        status: 'request',
      },
      {
        key: 'bamboo',
        name: 'BambooHR',
        desc: 'Keep recipient rosters current.',
        icon: Building2,
        status: 'request',
      },
    ],
  },
  {
    category: 'Cloud & Data',
    items: [
      {
        key: 'byod',
        name: 'Cloudflare (BYOD)',
        desc: 'Send from your own Cloudflare account.',
        icon: Cloud,
        status: 'request',
      },
      {
        key: 's3',
        name: 'Amazon S3 Export',
        desc: 'Scheduled event + report exports to a bucket.',
        icon: Database,
        status: 'request',
      },
    ],
  },
];

export function IntegrationsPage() {
  const navigate = useNavigate();
  const [requested, setRequested] = useState<Record<string, boolean>>({});

  return (
    <div>
      <PageHeader
        title="Integrations"
        subtitle="Connect Pavlov to your identity, productivity, security, and data tools."
      />
      <div className="space-y-8">
        {CATALOG.map((g) => (
          <section key={g.category}>
            <div className="eyebrow mb-3">{g.category}</div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {g.items.map((it) => {
                const Icon = it.icon;
                const done = requested[it.key];
                return (
                  <Card key={it.key} className="flex flex-col p-5">
                    <div className="flex items-start justify-between">
                      <span className="grid h-9 w-9 place-items-center rounded-lg border border-line text-ink">
                        <Icon className="h-[18px] w-[18px]" />
                      </span>
                      <Badge tone={it.status === 'live' ? 'solid' : 'muted'}>
                        {it.status === 'live' ? 'Live' : 'Available'}
                      </Badge>
                    </div>
                    <div className="mt-3 font-display text-lg text-ink">{it.name}</div>
                    <div className="mt-1 text-sm text-muted">{it.desc}</div>
                    <div className="flex-1" />
                    {it.status === 'live' ? (
                      <button
                        onClick={() => navigate(it.to!)}
                        className={cx('btn-outline mt-4 w-full')}
                      >
                        Manage <ArrowUpRight className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        onClick={() => setRequested((r) => ({ ...r, [it.key]: true }))}
                        disabled={done}
                        className="btn-outline mt-4 w-full"
                      >
                        {done ? (
                          <>
                            <Check className="h-4 w-4" /> Requested
                          </>
                        ) : (
                          'Request access'
                        )}
                      </button>
                    )}
                  </Card>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
